import { setCustomElementMetadata } from "@/lib/customElements";
import { cn } from "@/lib/utils";
import { BuildingService } from "@/services/building";
import { ClientService } from "@/services/client";
import { EventService } from "@/services/event";
import { InsuranceService } from "@/services/insurance";
import { UsersService } from "@/services/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDown, LinkIcon, Loader2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Badge } from "../base/badge";
import { Button } from "../base/button";
import { Card, CardContent } from "../base/card";
import { Checkbox } from "../base/checkbox";
import { Combobox } from "../base/combobox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../base/command";
import { DateTimePicker } from "../base/date-time-picker";
import { Input } from "../base/input";
import { Label } from "../base/label";
import { Popover, PopoverContent, PopoverTrigger } from "../base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { Textarea } from "../base/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../base/tooltip";
import { MultiSelect } from "../controls/multi-select";

import type {
  Building,
  Client,
  CreateEvent,
  Event,
  InsuranceClaim,
  UpdateEvent,
  User,
} from "@/models";
import { WORK_ORDER_TYPES } from "@/models/work-order";

// Work types - these should match your backend
const WORK_TYPES = ["Repair", "Replace", "New Construction"] as const;

// Event types - these should match your backend
const EVENT_TYPES = {
  INSPECTION: "Inspection",
  SERVICE: "Service Call",
  APPOINTMENT: "Client Appointment",
  INTERNAL: "Other",
  PERSONAL: "Personal Time",
  ADJUSTMENT: "Adjustment",
} as const;



const CAN_ASSIGN_PERSONAL_EVENTS_ROLES = [
  "Admin",
  "Front Office",
  "Office Manager",
  "Sales Manager",
  "Sales Manager:Production",
  "Project Manager",
];

// Form schema
const eventFormSchema = z.object({
  eventType: z.enum([
    EVENT_TYPES.INSPECTION,
    EVENT_TYPES.SERVICE,
    EVENT_TYPES.APPOINTMENT,
    EVENT_TYPES.INTERNAL,
    EVENT_TYPES.PERSONAL,
    EVENT_TYPES.ADJUSTMENT,
  ]),
  clientId: z.number().optional(),
  buildingId: z.number().optional(),
  claimJobId: z.number().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  workType: z.string().optional(),
  services: z.array(z.string()).optional(),
  assignedUserId: z.number().optional(),
  ownerUserId: z.number().optional(),
  invitedUserIds: z.array(z.number()).optional(),
  location: z.string().optional(),
  startDate: z.date({ required_error: "Start date is required" }),
  endDate: z.date({ required_error: "End date is required" }),
  isAllDay: z.boolean().default(false),
  duration: z.number().min(1, "Duration must be at least 1 minute").optional(),
  durationType: z.enum(["MINUTE", "HOUR", "DAY"]).default("MINUTE"),
}).refine((data) => {
  // For Internal events, at least one user should be invited
  if (data.eventType === EVENT_TYPES.INTERNAL && (!data.invitedUserIds || data.invitedUserIds.length === 0)) {
    return false;
  }
  return true;
}, {
  message: "Internal events must have at least one invited user",
  path: ["invitedUserIds"],
}).refine((data) => {
  // End date should be after start date
  if (data.endDate && data.startDate && data.endDate <= data.startDate) {
    return false;
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["endDate"],
});

type FormData = z.infer<typeof eventFormSchema>;

interface EventFormProps {
  apiUrl: string;
  apiStage: string;
  eventId?: number;
  preselectedClientId?: number;
  preselectedBuildingId?: number;
  isInsurance?: boolean;
  notes?: string;
  onSuccess?: (event: CreateEvent | Event) => void;
  onCancel?: () => void;
}

export function EventForm({
  apiUrl,
  apiStage,
  eventId,
  preselectedClientId,
  preselectedBuildingId,
  isInsurance,
  notes,
  onSuccess,
  onCancel,
}: EventFormProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [existingEvent, setExistingEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Individual loading states for each API call
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isLoadingBuildings, setIsLoadingBuildings] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingClaims, setIsLoadingClaims] = useState(true);

  // Client search state
  const [clientSearchResults, setClientSearchResults] = useState<Client[]>([]);
  const [isClientSearching, setIsClientSearching] = useState(false);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // User filter state for internal events
  const [userFilter, setUserFilter] = useState("");

  // Form submission error state
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Services
  const eventService = useMemo(
    () => new EventService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const clientService = useMemo(
    () => new ClientService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const buildingService = useMemo(
    () => new BuildingService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const usersService = useMemo(
    () => new UsersService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const insuranceService = useMemo(
    () => new InsuranceService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  let preselectedEventType: (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES] =
    EVENT_TYPES.APPOINTMENT;
  if (isInsurance) {
    preselectedEventType = EVENT_TYPES.ADJUSTMENT;
  }

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      eventType: preselectedEventType,
      isAllDay: false,
      duration: 60,
      durationType: "MINUTE",
      startDate: new Date(),
      endDate: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
      services: [],
      invitedUserIds: [],
      clientId: preselectedClientId || undefined,
      buildingId: preselectedBuildingId || undefined,
      description: notes || "",
    },
  });

  const eventType = watch("eventType");
  const clientId = watch("clientId");
  const startDate = watch("startDate");
  const duration = watch("duration");
  const durationType = watch("durationType");
  const isAllDay = watch("isAllDay");

  // Client search function
  const searchClients = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setClientSearchResults([]);
        return;
      }

      setIsClientSearching(true);
      try {
        const results = await clientService.searchClients(query);

        // Ensure we always have an array, even if the API returns null/undefined
        const safeResults = Array.isArray(results) ? results : [];
        setClientSearchResults(safeResults);
      } catch (error) {
        console.error("Failed to search clients:", error);
        setClientSearchResults([]);
      } finally {
        setIsClientSearching(false);
      }
    },
    [clientService]
  );

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load clients
        setIsLoadingClients(true);

        let eventData: Event | null = null;
        // If editing, load the existing event separately
        if (eventId) {
          eventData = await eventService.getEventById(eventId);
          setExistingEvent(eventData);
        }
        if (
          preselectedClientId ||
          eventData?.ecid ||
          eventData?.job?.client?.cid
        ) {
          const clientData = await clientService.getClientById(
            preselectedClientId ||
              (eventData?.ecid
                ? eventData.ecid
                : eventData?.job?.client?.cid) ||
              -1
          );
          setClients([clientData]);
          setSelectedClient(clientData);
          
          // If notes were not provided but client has notes, use client notes for description
          if (!notes && clientData.cnotes) {
            setValue("description", clientData.cnotes);
          }
        } else {
          // For search-based selection, we don't load all clients upfront
          setClients([]);
        }
        setIsLoadingClients(false);

        // Load users and claims in parallel
        const [usersData, claimsData] = await Promise.all([
          (async () => {
            setIsLoadingUsers(true);
            const data = await usersService.getAllUsers();
            setIsLoadingUsers(false);
            return data.filter((user) => user.ustatus !== "Disabled");
          })(),
          (async () => {
            setIsLoadingClaims(true);
            const data = await insuranceService.getInsuranceClaims();
            setIsLoadingClaims(false);
            return data;
          })(),
        ]);

        setUsers(usersData);

        // Filter claims to exclude approved and denied
        const activeClaims = claimsData.filter(
          (claim) =>
            !!claim.icstatus &&
            claim.icstatus !== "Approved (Final)" &&
            claim.icstatus !== "Denied (Final)"
        );
        setClaims(activeClaims);
      } catch (error) {
        console.error("Failed to load data:", error);
        // Reset loading states on error
        setIsLoadingClients(false);
        setIsLoadingUsers(false);
        setIsLoadingClaims(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [clientService, usersService, insuranceService, eventService, eventId, notes, preselectedClientId, setValue]);

  // Populate form when existing event and required data are loaded
  useEffect(() => {
    const populateForm = async () => {
      if (existingEvent && users.length > 0) {
        // Populate form with existing event data
        const eventTypeValue =
          Object.values(EVENT_TYPES).find(
            (type) => type === existingEvent.etype
          ) || EVENT_TYPES.APPOINTMENT;
        setValue("eventType", eventTypeValue);

        // Set client ID and load the client data
        if (existingEvent.ecid) {
          try {
            const clientData = await clientService.getClientById(
              existingEvent.ecid
            );
            setSelectedClient(clientData);
            setValue("clientId", existingEvent.ecid);
          } catch (error) {
            console.error("Failed to load client for existing event:", error);
          }
        }

        setValue("title", existingEvent.etitle || "");
        setValue("description", existingEvent.edesc || "");
        setValue("workType", existingEvent.eworktype || undefined);
        setValue("assignedUserId", existingEvent.euid || undefined);
        setValue("ownerUserId", existingEvent.eauid || undefined);
        setValue(
          "startDate",
          existingEvent.estartdts
            ? new Date(
                existingEvent.estartdts.getTime() +
                  existingEvent.estartdts.getTimezoneOffset() * 60000
              )
            : new Date()
        );
        setValue(
          "endDate",
          existingEvent.eenddts
            ? new Date(
                existingEvent.eenddts.getTime() +
                  existingEvent.eenddts.getTimezoneOffset() * 60000
              )
            : new Date()
        );
        setValue("isAllDay", existingEvent.eallday === "Yes");
        setValue("duration", existingEvent.eduration || 60);
        setValue(
          "durationType",
          (existingEvent.edurationtyp as "MINUTE" | "HOUR" | "DAY") || "MINUTE"
        );

        // Parse services if they exist
        if (existingEvent.eservices) {
          setValue(
            "services",
            existingEvent.eservices.split(":").filter(Boolean)
          );
        }

        // Parse invited users if they exist
        if (existingEvent.eauids) {
          const invitedUserIds = existingEvent.eauids.split(":").map((id) => parseInt(id));
          setValue("invitedUserIds", invitedUserIds);
        }
      }
    };

    populateForm();
  }, [existingEvent, users, setValue, clientService]);

  // Set claim ID for adjustment events when claims are loaded
  useEffect(() => {
    if (
      existingEvent &&
      existingEvent.etype === EVENT_TYPES.ADJUSTMENT &&
      claims.length > 0 &&
      existingEvent.elocid
    ) {
      // Find the claim that has the same building ID as the event
      const matchingClaim = claims.find(
        (claim) => claim.icjid === existingEvent.ejid
      );

      if (matchingClaim) {
        setValue("claimJobId", matchingClaim.icjid || undefined);
      }
    }
  }, [existingEvent, claims, setValue]);

  // Load buildings when client is selected
  useEffect(() => {
    const loadBuildings = async () => {
      if (clientId) {
        try {
          setIsLoadingBuildings(true);
          const buildingsData = await buildingService.getBuildingsByClientId(
            clientId
          );
          setBuildings(buildingsData);

          // Auto-select building if only one exists (but not when editing)
          if (buildingsData.length === 1 && !existingEvent) {
            setValue("buildingId", buildingsData[0].bid);
          }
        } catch (error) {
          console.error("Failed to load buildings:", error);
        } finally {
          setIsLoadingBuildings(false);
        }
      } else {
        setBuildings([]);
        setIsLoadingBuildings(false);
      }
    };

    loadBuildings();
  }, [
    clientId,
    clientService,
    buildingService,
    setValue,
    clients,
    existingEvent,
  ]);

  // Set building ID when buildings are loaded and we have an existing event
  useEffect(() => {
    if (existingEvent && buildings.length > 0 && existingEvent.elocid) {
      // Only set if the building exists in the loaded buildings
      const buildingExists = buildings.some(
        (b) => b.bid === existingEvent.elocid
      );

      if (buildingExists) {
        setValue("buildingId", existingEvent.elocid);
      }
    }
  }, [existingEvent, buildings, setValue]);

  // Update end date when start date or duration changes
  useEffect(() => {
    if (startDate) {
      if (isAllDay) {
        // For all-day events, set end date to end of the same day
        const endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        setValue("endDate", endDate);
      } else if (duration && durationType) {
        const durationMs =
          durationType === "HOUR"
            ? duration * 60 * 60 * 1000
            : durationType === "DAY"
            ? duration * 24 * 60 * 60 * 1000
            : duration * 60 * 1000;
        setValue("endDate", new Date(startDate.getTime() + durationMs));
      }
    }
  }, [startDate, duration, durationType, isAllDay, setValue]);

  // Handle claim selection
  const handleClaimSelection = async (claimJobId: number) => {
    const claim = claims.find((c) => c.icid === claimJobId);

    if (claim && claim.job?.jbid) {
      // Set building ID from the claim
      setValue("buildingId", claim.job.jbid);

      // Also set the client ID if it's not already set
      if (claim.job?.jcid && !watch("clientId")) {
        setValue("clientId", claim.job.jcid);
      }
    }
  };

  const closeModal = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).parent.postMessage(
      JSON.stringify({ type: "closeAllModals" }),
      "*"
    );
  };

  const handleCancel = () => {
    onCancel?.();
    closeModal();
  };

  const handleDelete = async () => {
    if (!existingEvent) return;

    try {
      setIsDeleting(true);
      setDeleteConfirmOpen(false);
      await eventService.deleteEvent(existingEvent.eid);
      closeModal();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).parent.postMessage(
        JSON.stringify({ type: "refreshCalendar" }),
        "*"
      );
    } catch (error) {
      console.error("Failed to delete event:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitError(null); // Clear any previous errors
      
      const user = JSON.parse(localStorage.getItem("raesUser") || "{}");

      if (existingEvent) {
        // Update existing event
        const updateData: UpdateEvent = {
          eid: existingEvent.eid,
          eauid: data.ownerUserId || existingEvent.eauid,
          etype: data.eventType,
          etitle: data.title || "",
          edesc: data.description || "",
          ejid: data.claimJobId,
          estartdts: new Date(
            data.startDate.getTime() -
              data.startDate.getTimezoneOffset() * 60000
          ),
          eenddts: new Date(
            data.endDate.getTime() - data.endDate.getTimezoneOffset() * 60000
          ),
          eallday: data.isAllDay ? "Yes" : "No",
          eduration: data.isAllDay ? null : data.duration,
          edurationtyp: data.isAllDay ? null : data.durationType,
          ecid: data.clientId,
          euid: data.assignedUserId,
          eworktype: data.workType,
          elocid: data.buildingId,
          eservices: data.services?.join(":"),
          // Handle invited users for Internal events
          eauids:
            data.eventType === EVENT_TYPES.INTERNAL && data.invitedUserIds
              ? users
                  .filter((u) => data.invitedUserIds?.includes(u.uid))
                  .map((u) => u.uid.toString())
                  .filter(Boolean)
                  .join(":")
              : undefined,
        };

        const event = await eventService.updateEvent(updateData);
        onSuccess?.(event);
      } else {
        // Create new event
        const eventData: CreateEvent = {
          etype: data.eventType,
          eauid: data.ownerUserId || parseInt(user.uid),
          etitle: data.title || "",
          edesc: data.description || "",
          ejid: data.claimJobId,
          estartdts: new Date(
            data.startDate.getTime() -
              data.startDate.getTimezoneOffset() * 60000
          ),
          eenddts: new Date(
            data.endDate.getTime() - data.endDate.getTimezoneOffset() * 60000
          ),
          eadts: new Date(), // Added date, can be set to current date if needed
          eallday: data.isAllDay ? "Yes" : "No",
          eduration: data.isAllDay ? null : data.duration,
          edurationtyp: data.isAllDay ? null : data.durationType,
          ecid: data.clientId,
          euid: data.assignedUserId,
          eworktype: data.workType,
          estatus: "Active",
          elocid: data.buildingId,
          eservices: data.services?.join(":"),
          // Handle invited users for Internal events
          eauids:
            data.eventType === EVENT_TYPES.INTERNAL && data.invitedUserIds
              ? users
                  .filter((u) => data.invitedUserIds?.includes(u.uid))
                  .map((u) => u.uid.toString())
                  .filter(Boolean)
                  .join(":")
              : undefined,
        };

        const event = await eventService.createEvent(eventData);
        onSuccess?.(event);
      }

      closeModal();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).parent.postMessage(
        JSON.stringify({ type: "refreshCalendar" }),
        "*"
      );
    } catch (error) {
      console.error(
        `Failed to ${existingEvent ? "update" : "create"} event:`,
        error
      );
      
      // Set user-friendly error message
      if (error instanceof Error) {
        setSubmitError(`Failed to ${existingEvent ? "update" : "create"} event: ${error.message}`);
      } else {
        setSubmitError(`Failed to ${existingEvent ? "update" : "create"} event. Please try again.`);
      }
    }
  };



  const openClientDashboard = (clientId: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const thisWindow = window as any;

    thisWindow.clr("all");
    thisWindow.$("#modal-container").modal("show");
    thisWindow.$("form").validate().settings.ignore = "*";
    thisWindow.goM(
      "raes-clientsView",
      "viewContracts1",
      clientId.toString(),
      null
    );
  };

  const user = JSON.parse(localStorage.getItem("raesUser") || "{}");

  const shouldShowClientSelection =
    eventType === EVENT_TYPES.INSPECTION ||
    eventType === EVENT_TYPES.SERVICE ||
    eventType === EVENT_TYPES.APPOINTMENT ||
    eventType === EVENT_TYPES.ADJUSTMENT;
  const shouldShowLocationSelection = shouldShowClientSelection;
  const shouldShowWorkType =
    eventType === EVENT_TYPES.INSPECTION || eventType === EVENT_TYPES.SERVICE;
  const shouldShowServices =
    eventType === EVENT_TYPES.INSPECTION || eventType === EVENT_TYPES.SERVICE;
  const shouldShowUserAssignment =
    eventType === EVENT_TYPES.INSPECTION ||
    eventType === EVENT_TYPES.SERVICE ||
    eventType === EVENT_TYPES.ADJUSTMENT ||
    eventType === EVENT_TYPES.APPOINTMENT;
  const shouldShowPersonalTimeAssignment =
    eventType === EVENT_TYPES.PERSONAL &&
    CAN_ASSIGN_PERSONAL_EVENTS_ROLES.some((role) =>
      user?.uroles?.split(":").includes(role)
    );
  const shouldShowMultiUserSelection = eventType === EVENT_TYPES.INTERNAL;
  const shouldShowClaimSelection = eventType === EVENT_TYPES.ADJUSTMENT;
  const shouldShowTitle =
    eventType === EVENT_TYPES.APPOINTMENT ||
    eventType === EVENT_TYPES.INTERNAL ||
    eventType === EVENT_TYPES.PERSONAL;

  const salesUsers = users
    .filter((u) => u.ustatus !== "Disabled")
    .sort((a, b) => (a.ufirstn ?? "").localeCompare(b.ufirstn ?? ""));
  const installerUsers = users
    .filter((u) => u.ustatus !== "Disabled")
    .sort((a, b) => (a.ufirstn ?? "").localeCompare(b.ufirstn ?? ""));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-16 max-w-2xl h-full">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card className="w-full max-w-2xl border-0 shadow-none">
      <CardContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
          noValidate
          id="eventForm"
        >
          {/* Event Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="eventType">Event Type</Label>
            <Controller
              name="eventType"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EVENT_TYPES.APPOINTMENT}>
                      Client Appointment
                    </SelectItem>
                    <SelectItem value={EVENT_TYPES.INSPECTION}>
                      Inspection
                    </SelectItem>
                    {/* <SelectItem value={EVENT_TYPES.SERVICE}>Service Call</SelectItem> */}
                    <SelectItem value={EVENT_TYPES.INTERNAL}>
                      Internal
                    </SelectItem>
                    <SelectItem value={EVENT_TYPES.PERSONAL}>
                      Personal
                    </SelectItem>
                    <SelectItem value={EVENT_TYPES.ADJUSTMENT}>
                      Adjustment
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.eventType && (
              <p className="text-sm text-red-600">{errors.eventType.message}</p>
            )}
          </div>

          {/* Client Selection */}
          {shouldShowClientSelection && (
            <div className="space-y-2">
              <div className="flex gap-2 items-center">
                <Label htmlFor="clientId">
                  Client
                  {selectedClient && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            className="ml-2"
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              openClientDashboard(selectedClient?.cid || 0)
                            }
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Open Client Dashboard</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </Label>
                {(isLoadingClients || isClientSearching) && (
                  <Loader2 className="mb-2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <Popover
                    open={clientSearchOpen}
                    onOpenChange={setClientSearchOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={clientSearchOpen}
                        className="justify-between w-full"
                        disabled={isLoadingClients}
                      >
                        {selectedClient
                          ? selectedClient.ccontact
                          : "Search for client..."}
                        <ChevronDown className="ml-2 w-4 h-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-full">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search clients..."
                          onValueChange={(value) => {
                            searchClients(value);
                          }}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {isClientSearching
                              ? "Searching..."
                              : "No clients found."}
                          </CommandEmpty>
                          <CommandGroup className="overflow-auto max-h-60">
                            {Array.isArray(clientSearchResults) &&
                              clientSearchResults.map((client) => (
                                <CommandItem
                                  key={client.cid}
                                  value={client.cid.toString()}
                                  onSelect={() => {
                                    setSelectedClient(client);
                                    field.onChange(client.cid);
                                    setClientSearchOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.value === client.cid
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {client.ccontact}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              />
              {errors.clientId && (
                <p className="text-sm text-red-600">{errors.clientId.message}</p>
              )}
            </div>
          )}

          {/* Claim Selection for Adjustments */}
          {shouldShowClaimSelection && (
            <div className="space-y-2">
              <div className="flex gap-2 items-center">
                <Label htmlFor="claimJobId">Insurance Claim</Label>
                {isLoadingClaims && (
                  <Loader2 className="mb-2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <Controller
                name="claimJobId"
                control={control}
                render={({ field }) => (
                  <Select
                    disabled={!clientId || isLoadingClaims}
                    onValueChange={(value) => {
                      field.onChange(parseInt(value));
                      handleClaimSelection(parseInt(value));
                    }}
                    value={field.value?.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoadingClaims
                            ? "Loading claims..."
                            : clientId
                            ? "Select a claim"
                            : "Select a client first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {claims
                        .filter((claim) => clientId === claim.job?.jcid)
                        .map((claim) => (
                          <SelectItem
                            key={claim.icid}
                            value={claim.icjid?.toString() || ""}
                          >
                            <div className="flex justify-between items-center w-full">
                              <span>
                                {claim.icclaimnum || `Claim #${claim.icid}`}
                              </span>
                              <Badge variant="secondary" className="ml-2">
                                {claim.icstatus}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.claimJobId && (
                <p className="text-sm text-red-600">{errors.claimJobId.message}</p>
              )}
            </div>
          )}

          {/* Location Selection */}
          {shouldShowLocationSelection && (
            <div className="space-y-2">
              <div className="flex gap-2 items-center">
                <Label htmlFor="buildingId">Location</Label>
                {isLoadingBuildings && (
                  <Loader2 className="mb-2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <Controller
                name="buildingId"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value?.toString()}
                    disabled={
                      !buildings.length ||
                      eventType === EVENT_TYPES.ADJUSTMENT ||
                      isLoadingBuildings
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoadingBuildings
                            ? "Loading locations..."
                            : "Select location"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map((building) => (
                        <SelectItem
                          key={building.bid}
                          value={building.bid.toString()}
                        >
                          {`${building.baddr1}, ${building.bcity}, ${building.bst} ${building.bzip}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.buildingId && (
                <p className="text-sm text-red-600">{errors.buildingId.message}</p>
              )}
            </div>
          )}

          {/* User Assignment */}
          {shouldShowUserAssignment && (
            <div className="flex flex-col space-y-2">
              <div className="flex gap-2 items-center">
                <Label htmlFor="assignedUserId">
                  {eventType !== EVENT_TYPES.SERVICE
                    ? "Assign Sales Rep"
                    : "Assign Installer"}
                </Label>
                {isLoadingUsers && (
                  <Loader2 className="mb-2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <Controller
                name="assignedUserId"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={
                      isLoadingUsers
                        ? []
                        : ((eventType === EVENT_TYPES.INSPECTION || eventType === EVENT_TYPES.ADJUSTMENT)
                            ? salesUsers
                            : installerUsers
                          ).map((user) => ({
                            value: user.uid.toString(),
                            label: `${user.ufirstn} ${user.ulastn}`,
                          }))
                    }
                    placeholder={
                      isLoadingUsers
                        ? "Loading users..."
                        : `Search ${
                            (eventType === EVENT_TYPES.INSPECTION || eventType === EVENT_TYPES.ADJUSTMENT)
                              ? "sales rep"
                              : "installer"
                          }...`
                    }
                    emptyMessage={`No ${
                      (eventType === EVENT_TYPES.INSPECTION || eventType === EVENT_TYPES.ADJUSTMENT)
                        ? "sales reps"
                        : "installers"
                    } found.`}
                    value={field.value?.toString() || ""}
                    onChange={(value) =>
                      field.onChange(value ? parseInt(value) : undefined)
                    }
                    name="assignedUserId"
                    width="100%"
                  />
                )}
              />
              {errors.assignedUserId && (
                <p className="text-sm text-red-600">{errors.assignedUserId.message}</p>
              )}
            </div>
          )}

          {/* Personal Time Assignment */}
          {shouldShowPersonalTimeAssignment && (
            <div className="flex flex-col space-y-2">
              <div className="flex gap-2 items-center">
                <Label htmlFor="ownerUserId">
                  Assign Personal Time
                </Label>
                {isLoadingUsers && (
                  <Loader2 className="mb-2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <Controller
                name="ownerUserId"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={
                      isLoadingUsers
                        ? []
                        : users.map((user) => ({
                            value: user.uid.toString(),
                            label: `${user.ufirstn} ${user.ulastn}`,
                          }))
                    }
                    placeholder={
                      isLoadingUsers
                        ? "Loading users..."
                        : `Search users...`
                    }
                    emptyMessage={`No users found.`}
                    value={field.value?.toString() || ""}
                    onChange={(value) =>
                      field.onChange(value ? parseInt(value) : undefined)
                    }
                    name="ownerUserId"
                    width="100%"
                  />
                )}
              />
              {errors.ownerUserId && (
                <p className="text-sm text-red-600">{errors.ownerUserId.message}</p>
              )}
            </div>
          )}

          {/* Work Type */}
          {shouldShowWorkType && (
            <div className="space-y-2">
              <Label htmlFor="workType">Work Type</Label>
              <Controller
                name="workType"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select work type" />
                    </SelectTrigger>
                    <SelectContent>
                      {WORK_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.workType && (
                <p className="text-sm text-red-600">{errors.workType.message}</p>
              )}
            </div>
          )}

          {/* Services */}
          {shouldShowServices && (
            <div className="space-y-2">
              <Label>Services</Label>
              <MultiSelect
                options={WORK_ORDER_TYPES.map((service) => ({
                  label: service,
                  value: service,
                }))}
                onValueChange={(values) => setValue("services", values)}
                defaultValue={watch("services") || []}
                placeholder="Select services"
                maxCount={2}
              />
              {errors.services && (
                <p className="text-sm text-red-600">{errors.services.message}</p>
              )}
            </div>
          )}

          {/* Title */}
          {shouldShowTitle && (
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                {...register("title")}
                placeholder={
                  eventType === EVENT_TYPES.PERSONAL
                    ? "Event title"
                    : "Optional title"
                }
              />
              {errors.title && (
                <p className="text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>
          )}

          {/* Description/Notes */}
          <div className="space-y-2">
            <Label htmlFor="description">Notes</Label>
            <Textarea
              {...register("description")}
              placeholder="Additional notes..."
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          {/* Date and Time */}
            <div className="space-y-2">
              <Label>Start Date & Time</Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <div className="flex">
                  <DateTimePicker
                    value={field.value}
                    onChange={field.onChange}
                  />
                  </div>
                )}
              />
              {errors.startDate && (
                <p className="text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>
            <div className="flex items-end space-x-2">
              <div className="space-y-2">
                <Label>Duration</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    {...register("duration", { valueAsNumber: true })}
                    min={1}
                    className="w-20"
                    disabled={isAllDay}
                    placeholder={isAllDay ? "N/A" : "60"}
                  />
                  {errors.duration && (
                    <p className="text-sm text-red-600">{errors.duration.message}</p>
                  )}
                  <Controller
                    name="durationType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isAllDay}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MINUTE">mins</SelectItem>
                          <SelectItem value="HOUR">hours</SelectItem>
                          <SelectItem value="DAY">days</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.durationType && (
                    <p className="text-sm text-red-600">{errors.durationType.message}</p>
                  )}
                  {errors.endDate && (
                    <p className="text-sm text-red-600">{errors.endDate.message}</p>
                  )}
                  <div className="flex gap-2 items-center ml-2">
                    <Controller
                      name="isAllDay"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="isAllDay"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                    <Label
                      htmlFor="isAllDay"
                      className="m-0 text-sm font-normal"
                    >
                      All Day
                    </Label>
                  </div>
                </div>
              </div>
          </div>

          {/* Multi-user Selection for Internal Events */}
          {shouldShowMultiUserSelection && (
            <div className="space-y-2">
              <Label>Invite Users</Label>
              
              {/* Selected Users Section */}
              {(watch("invitedUserIds") || []).length > 0 && (
                <div className="p-3 bg-gray-50 rounded border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">
                      Selected Users ({(watch("invitedUserIds") || []).length})
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setValue("invitedUserIds", [])}
                      className="px-2 h-6 text-xs"
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {users
                      .filter((user) => (watch("invitedUserIds") || []).includes(user.uid))
                      .sort((a, b) => (a.ufirstn || "").localeCompare(b.ufirstn || ""))
                      .map((user) => (
                        <Badge
                          key={user.uid}
                          variant="secondary"
                          className="flex gap-1 items-center text-xs"
                        >
                          {user.ufirstn} {user.ulastn}
                          <button
                            type="button"
                            onClick={() => {
                              const currentUsers = watch("invitedUserIds") || [];
                              setValue(
                                "invitedUserIds",
                                currentUsers.filter((id) => id !== user.uid)
                              );
                            }}
                            className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Filter users..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const allUserIds = users
                      .filter((u) => u.ustatus !== "Disabled")
                      .map((u) => u.uid);
                    setValue("invitedUserIds", allUserIds);
                  }}
                  className="px-3 text-xs"
                >
                  Select All
                </Button>
              </div>
              <div className="grid overflow-y-auto grid-cols-2 gap-2 p-2 max-h-48 rounded border">
                {users
                  .filter((user) => {
                    if (!userFilter) return true;
                    const searchTerm = userFilter.toLowerCase();
                    const fullName = `${user.ufirstn || ""} ${user.ulastn || ""}`.toLowerCase();
                    return fullName.includes(searchTerm);
                  })
                  .sort((a, b) => (a.ufirstn || "").localeCompare(b.ufirstn || ""))
                  .map((user) => (
                    <label key={user.uid} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        value={user.uid}
                        checked={(watch("invitedUserIds") || []).includes(user.uid)}
                        onChange={(e) => {
                          const currentUsers = watch("invitedUserIds") || [];
                          if (e.target.checked) {
                            setValue("invitedUserIds", [
                              ...currentUsers,
                              user.uid,
                            ]);
                          } else {
                            setValue(
                              "invitedUserIds",
                              currentUsers.filter((id) => id !== user.uid)
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">
                        {user.ufirstn} {user.ulastn}
                      </span>
                    </label>
                  ))}
              </div>
              {users.filter((user) => {
                if (!userFilter) return true;
                const searchTerm = userFilter.toLowerCase();
                const fullName = `${user.ufirstn || ""} ${user.ulastn || ""}`.toLowerCase();
                return fullName.includes(searchTerm);
              }).length === 0 && userFilter && (
                <p className="py-2 text-sm text-center text-muted-foreground">
                  No users found matching "{userFilter}"
                </p>
              )}
              {errors.invitedUserIds && (
                <p className="text-sm text-red-600">{errors.invitedUserIds.message}</p>
              )}
            </div>
          )}

          {/* General form submission error */}
          {submitError && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded border border-red-200">
              {submitError}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-between items-center pt-4">
            {/* Delete button on the left (only show when editing) */}
            {existingEvent && (
              <Popover
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={isDeleting || isSubmitting || isLoading}
                  >
                    {isDeleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">Delete Event</h4>
                      <p className="text-sm text-muted-foreground">
                        Are you sure you want to delete this event? This action
                        cannot be undone.
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteConfirmOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          "Delete Event"
                        )}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Cancel and Submit buttons on the right */}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isLoading || isDeleting}
              >
                {isSubmitting
                  ? existingEvent
                    ? "Updating..."
                    : "Creating..."
                  : existingEvent
                  ? "Update Event"
                  : "Create Event"}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

EventForm.displayName = "EventForm";

setCustomElementMetadata({
  component: EventForm,
  tagName: "raes-event-form",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
    "event-id": (value: string) => ({
      value: parseInt(value),
      prop: "eventId",
    }),
    "client-id": (value: string) => ({
      value: parseInt(value),
      prop: "preselectedClientId",
    }),
    "building-id": (value: string) => ({
      value: parseInt(value),
      prop: "preselectedBuildingId",
    }),
    "is-insurance": (value: string) => ({
      value: value === "1",
      prop: "isInsurance",
    }),
    notes: (value: string) => ({
      value,
      prop: "notes",
    }),
  },
});
